import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ShieldCheck, Phone, Lock, Key } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appName, setAppName] = useState('DWS Vault Chat');
  const [pendingVerification, setPendingVerification] = useState(null);
  const [accessCode, setAccessCode] = useState('');

  const { login, user } = useAuth();

  // Fetch app name
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API}/settings/support-info`);
        if (response.data.app_name) {
          setAppName(response.data.app_name);
          document.title = `${response.data.app_name} - Admin Login`;
        }
      } catch (error) {
        console.error('Failed to fetch app name:', error);
      }
    };
    fetchAppName();
  }, []);

  const navigate = useNavigate();

  const getDeviceId = () => {
    let deviceId = localStorage.getItem('admin_device_id');
    if (!deviceId) {
      deviceId = 'dev_' + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2) + Date.now());
      localStorage.setItem('admin_device_id', deviceId);
    }
    return deviceId;
  };

  const finishLogin = async (userData) => {
    if (userData.role !== 'super_admin') {
      setError('This is Admin login page. Please use the correct login page for your role.');
      return;
    }
    login(userData);
    try {
      await axios.post(`${API}/admin/activity/track`, {
        user_id: userData.id,
        action: 'login'
      });
    } catch (trackErr) {
      console.error('Activity tracking failed:', trackErr);
    }
    navigate('/admin-dashboard');
  };

  const startGoogleVerification = () => {
    sessionStorage.setItem('admin_2fa_temp_token', pendingVerification.tempToken);
    sessionStorage.setItem('admin_2fa_admin_id', pendingVerification.adminId);
    sessionStorage.setItem('admin_2fa_device_id', pendingVerification.deviceId);
    const redirectUrl = window.location.origin + '/admin-auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const verifyAccessCode = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/verify-access-code`, {
        temp_token: pendingVerification.tempToken,
        access_code: accessCode,
        device_id: pendingVerification.deviceId,
      });
      await finishLogin(response.data);
      setPendingVerification(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Access code verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Auto-redirect only if already logged in AS ADMIN (matching role)
  // If user is logged in as different role, allow them to see this page and log in as admin
  useEffect(() => {
    if (user && user.role === 'super_admin') {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get or generate persistent device_id (one per browser)
      const deviceId = getDeviceId();

      const response = await axios.post(`${API}/auth/login`, {
        phone,
        password,
        device_id: deviceId,
      });

      const data = response.data;

      // ADMIN 2FA: Server signals that Google verification is required
      if (data.requires_google_verification) {
        setPendingVerification({ tempToken: data.temp_token, adminId: data.admin_id, deviceId });
        return;
      }

      // Trusted device path — regular login flow
      const userData = data;

      await finishLogin(userData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center p-4" style={{ position: 'fixed', inset: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md my-8">
        <div className="text-center mb-8">
          <div className="w-28 h-28 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/icons/customer/icon-128x128.png" 
              alt="DWS Vault Chat"
              className="w-28 h-28 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">{appName}</h1>
          <p className="text-gray-600 mt-2">Admin Login</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          {error && (
            <div className="bg-wa-hover border border-red-200 text-wa-text-primary px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {pendingVerification ? (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <div className="bg-wa-hover border border-purple-200 text-wa-text-primary px-3 py-2 rounded-lg text-xs flex items-start gap-2">
                <ShieldCheck size={16} weight="fill" className="text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Choose how to verify this new admin device.</span>
              </div>
              <button
                type="button"
                onClick={startGoogleVerification}
                disabled={loading}
                className="w-full border border-purple-500 text-purple-700 font-semibold py-3 rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50"
              >
                Verify with admin Gmail
              </button>
              <div className="flex items-center gap-2 text-xs text-gray-400"><span className="h-px bg-gray-200 flex-1" />or<span className="h-px bg-gray-200 flex-1" /></div>
              <div className="relative">
                <Key size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 8-digit access code"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                />
              </div>
              <button
                type="button"
                onClick={verifyAccessCode}
                disabled={loading || accessCode.length !== 8}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify access code'}
              </button>
            </div>
          ) : (
            <>
              {/* Google 2FA notice */}
              <div className="bg-wa-hover border border-purple-200 text-wa-text-primary px-3 py-2 rounded-lg text-xs flex items-start gap-2">
                <ShieldCheck size={16} weight="fill" className="text-purple-600 mt-0.5 flex-shrink-0" />
                <span>
                  On a new device, verify with your admin Gmail or a 12-hour access code generated in Admin Settings.
                </span>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </>
          )}

          {/* Forgot Password Link */}
          <button
            type="button"
            onClick={() => navigate('/admin-forgot-password')}
            className="w-full text-sm text-purple-600 hover:text-wa-text-primary font-medium mt-2"
            data-testid="admin-forgot-password-link"
          >
            Forgot Password?
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
